import { useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { Form } from "@dreambigger/design-system/src/components";
import {
  PlaidIDVStepAssets,
  ResponseFields,
  Step,
} from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import styles from "./steps.module.scss";
import StepWrapper from "./step-wrapper";
import { useApplication, usePlaid } from "../api";
import { Button } from "@dreambigger/design-system/src/components";
import {
  CheckCircleFilled,
  WarningFilled,
  CameraFilled,
  PlayCircleFilled,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
import { message, Spin } from "antd";

import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnEvent,
} from "react-plaid-link";

const idvSessionIdKey = "idvSessionId";
const linkSessionIdKey = "linkSessionId";
const statusKey = "status";

enum Status {
  Pass = "pass",
  Fail = "fail",
  Shareable = "shareable",
}

const sessionEventToStatus: Record<string, string> = {
  IDENTITY_VERIFICATION_PASS_SESSION: "pass",
  IDENTITY_VERIFICATION_FAIL_SESSION: "fail",
};

const spinnerIcon = <Loading3QuartersOutlined spin />;

// The tint alone carries the signal, so the foreground is free to be dark
// enough to read comfortably: 6.8:1 success, 6.5:1 warning, 8.0:1 info, all
// past AA. Info is blue rather than green because a shareable session is still
// pending — green would read as "you're done" when the applicant has yet to
// verify from their email.
const calloutTones = {
  success: { bg: "#ECFDF3", fg: "#166534" },
  warning: { bg: "#FEF9E7", fg: "#854D0E" },
  info: { bg: "#EFF6FF", fg: "#1E40AF" },
};

// Height of the title's own line box (f-4 at lh-3), so the icon centers on the
// first line of text rather than on the block as a whole.
const calloutIconHeight = 22;

function Callout({
  tone,
  icon,
  title,
  children,
}: {
  tone: keyof typeof calloutTones;
  icon: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  const { bg, fg } = calloutTones[tone];

  return (
    <div
      className="flex items-start pa-3 br-2 mb-5"
      style={{ backgroundColor: bg, color: fg, maxWidth: "26rem" }}
    >
      <span
        className="f-5 flex items-center"
        style={{ height: calloutIconHeight }}
      >
        {icon}
      </span>
      <div className="ml-2">
        <p className="mb-0 f-4 fwb lh-3">{title}</p>
        {children && <p className="mb-0 mt-1 f-3 lh-4">{children}</p>}
      </div>
    </div>
  );
}

export default function PlaidIdv({ flow, step, brand, progress }: StepProps) {
  const {
    slug,
    type,
    assets: {
      templateUuid,
      helpText1,
      helpText2,
      termsText,
      ctaText,
      errorText,
      allowContinueOnError,
      maskError,
    },
  }: Omit<Step, "assets"> & { assets: PlaidIDVStepAssets } = step;

  // state
  const [status, setStatus] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const autoAdvance = useRef(false);

  // Ref to track if initialize has been run
  const initializeRef = useRef<boolean>(false);

  // custom hooks
  const [form] = Form.useForm();
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const plaidHelper = usePlaid(flow.financialInstitution.id);

  const initialize = async () => {
    try {
      const response = applicationHelper.findResponseField(
        flow.id,
        "draft",
        slug,
        "status"
      );
      if (typeof response === "string") {
        setStatus(response);
      } else {
        // If not completed, initialize Plaid
        if (!templateUuid) {
          message.error("Error initializing ID Verification");
          return;
        }

        const application = applicationHelper.find(flow.id, "draft");
        if (!application) {
          message.error("Unable to find application");
          return;
        }

        const idvSessionData = await plaidHelper.createLinkTokenforIDV({
          requestId: `${application.id}|${slug}`,
          templateUuid,
          onboardingApplicationId: application.id,
        });

        if (!idvSessionData) {
          message.error("Error initializing ID Verification");
          return;
        }

        // update the form field value for the ID Verification Session
        // clear out existing form values by using setFieldsValue
        form.setFieldsValue({ [idvSessionIdKey]: idvSessionData.id });

        if ("linkToken" in idvSessionData) {
          // initialize link session
          setToken(idvSessionData.linkToken);
        } else if (idvSessionData.isShareable === true) {
          changeStatus(Status.Shareable);
        }
      }
    } catch (error) {
      message.error("Error initializing ID Verification");
      console.error("Error initializing ID Verification", error);
    }
  };

  const changeStatus = async (newStatus: string) => {
    // on pass or fail, update the application with the response fields
    // otherwise if the user hits back instead of next, it will mess it up
    const application = applicationHelper.find(flow.id, "draft");
    if (!application) {
      message.error("Unable to find application");
      return;
    }

    // update the form field value for status
    form.setFieldValue(statusKey, newStatus);

    // Retrieve the current form field values
    const values = form.getFieldsValue();

    //remove empty fields
    const filteredResponse: ResponseFields = {};
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        return;
      }

      filteredResponse[key] = values[key];
    });

    // update via API
    const saved = await applicationHelper.update(application.id, {
      slug,
      type,
      fields: filteredResponse,
    });
    if (!saved) {
      //TODO: handle scenario if the application is not saved
      return;
    }
    // update state and enable auto-advance
    autoAdvance.current = true;
    setStatus(newStatus);
  };

  // initialize when component mounts
  useEffect(() => {
    if (!applicationHelper.isValidating && !initializeRef.current) {
      initialize();
      initializeRef.current = true;
    }
  }, [applicationHelper.isValidating]);

  // Initialize Plaid Link when linkToken is set
  useEffect(() => {
    if (token) {
      setReady(true);
    }
  }, [token]);

  // triggered by both onSuccess and onEvent handlers, as well as init code
  useEffect(() => {
    if (!status) {
      return;
    }

    setReady(true);

    const canProceed =
      status === Status.Pass ||
      status === Status.Shareable ||
      (status === Status.Fail && allowContinueOnError);

    if (canProceed) {
      setDisabled(false);

      // Advance only when there is nothing to read: a pass, or a failure this
      // step is configured to hide. Anything else leaves a message on screen,
      // so the applicant continues with the button instead.
      if (
        autoAdvance.current &&
        (status === Status.Pass || (status === Status.Fail && maskError))
      ) {
        form.submit();
      }
    }
  }, [status, allowContinueOnError, maskError, form]);

  // Plaid IDV callbacks
  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    (
      _publicToken: string,
      { link_session_id: newLinkSessionId }: PlaidLinkOnSuccessMetadata
    ) => {
      // update the form field value for the Link Session
      form.setFieldValue(linkSessionIdKey, newLinkSessionId);
    },
    [form]
  );

  const onEvent = useCallback<PlaidLinkOnEvent>(
    async (eventName: string) => {
      const newStatus = sessionEventToStatus[eventName];
      if (newStatus) {
        changeStatus(newStatus);
      }
    },
    [form, changeStatus]
  );

  const { open: launchPlaid, ready: plaidIsReady } = usePlaidLink({
    token,
    onSuccess,
    onEvent,
  });

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled,
    processing: !ready,
  };

  if (!templateUuid) {
    return (
      <p>
        There was an error loading this page. Please contact support to finish
        your application.
      </p>
    );
  }

  return (
    <StepWrapper {...wrapperProps}>
      <Form.Item name={idvSessionIdKey} hidden={true} />
      <Form.Item name={linkSessionIdKey} hidden={true} />
      <Form.Item name={statusKey} hidden={true} />

      {!plaidIsReady && !status && <Spin indicator={spinnerIcon} />}

      {(plaidIsReady || !!status) && (
        <>
          {helpText1 && (
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4`}
                dangerouslySetInnerHTML={{
                  __html: helpText1 ?? "",
                }}
              />
            </div>
          )}

          <div className={`flex flex-column justify-center items-center pt-4`}>
            {/* If verified display the result. Otherwise give option to load IDV */}{" "}
            {status === null && (
              <Button
                type="primary"
                className="f-5 h-7 md_f-6 md_h-8 s-2 mb-7 lift"
                onClick={() => launchPlaid()}
                disabled={!plaidIsReady}
              >
                <CameraFilled /> {ctaText ?? "Verify Using Your Webcam"}
              </Button>
            )}
            {status === Status.Shareable && (
              // Message displayed on url sent.
              <Callout
                tone="info"
                icon={<PlayCircleFilled />}
                title="Verification in progress"
              >
                Please check your email for instructions on how to verify your
                identity.
              </Callout>
            )}
            {(status === Status.Pass ||
              (status === Status.Fail && maskError)) && (
              // Message displayed on success or hiding error
              <Callout
                tone="success"
                icon={<CheckCircleFilled />}
                title="Verification complete"
              />
            )}
            {status === Status.Fail && !maskError && (
              // Message displayed on failure. Amber rather than red: the
              // applicant usually still has a path forward from here.
              <Callout
                tone="warning"
                icon={<WarningFilled />}
                title="We couldn't verify your ID"
              >
                {errorText ||
                  (allowContinueOnError
                    ? "You can continue your application using the button below."
                    : "Please go back to the previous step and verify your ID through another option.")}
              </Callout>
            )}
          </div>

          {helpText2 && (
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
                dangerouslySetInnerHTML={{
                  __html: helpText2,
                }}
              />
            </div>
          )}

          {termsText && (
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
                dangerouslySetInnerHTML={{
                  __html: termsText,
                }}
              />
            </div>
          )}
        </>
      )}
    </StepWrapper>
  );
}
